/**
 * UnifiedCarousel — desktop-only single Swiper spanning all projects.
 *
 * All project images are flattened into one Swiper instance. The carousel
 * is position:sticky so it stays in the viewport while the outer scroll
 * range div provides the scroll distance. Progress is read directly from
 * window.scrollY in the GSAP ticker — no ScrollTrigger, no pin spacer,
 * no refresh jitter.
 *
 * API returns projects newest-first; slides[0] = newest project's first image.
 * Progress 0→1 maps translate min→max (Swiper centeredSlides: minTranslate()
 * is the positive value centering slide[0]; maxTranslate() is the large
 * negative value centering slide[N-1]). Result: scroll=0 shows newest,
 * scrolling down advances toward oldest. Images within each project appear
 * in DEVICE_ORDER (generic → desktop → mobile → tablet).
 *
 * getScrollYForProject uses rangeEl.getBoundingClientRect().top + scrollY
 * because sticky elements have no reliable offsetTop.
 *
 * Active project changes fire into sectionScrollStore so FixedInfoPanel and
 * LeftSidebar stay in sync automatically.
 */
import "swiper/css"
import gsap from "gsap"
import { startTransition, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react"
import { createPortal } from "react-dom"
import { Swiper, SwiperSlide } from "swiper/react"
import type { Swiper as SwiperClass } from "swiper"
import type { Project, ProjectImage } from "@/types/portfolio"
import { useSectionScrollStore } from "@/stores/sectionScrollStore"
import { globalLenis } from "@/lib/lenis"
import { CAROUSEL_H, SLIDE_WIDTHS, type CarouselItem, ExpandedOverlay } from "./ProjectGallery"
import { DeviceFrame, FRAME_PADDING, type DeviceType } from "./DeviceFrame"

const FEATURE_TEXT_H = 80

const MIN_DWELL = 4
export const SCROLL_PER_SLIDE = 280


const DEVICE_ORDER: DeviceType[] = ["generic", "desktop", "mobile", "tablet"]

type UnifiedSlide = {
  img: ProjectImage
  project: Project
  projectIndex: number
  isFirstInProject: boolean
  deviceType: DeviceType
}

/**
 * Builds the flat slide array for the unified carousel.
 *
 * API returns projects newest-first. Outer loop is forward (pi 0→N-1) so
 * slides[0] is the newest project's first image — Formula 1 centers it at
 * progress=0. Inner loop is forward (i 0→count-1) so images appear in
 * DEVICE_ORDER and isFirstInProject lands at the START of each batch.
 */
function buildSlides(projects: Project[]): UnifiedSlide[] {
  const slides: UnifiedSlide[] = []

  for (let pi = 0; pi < projects.length; pi++) {
    const project = projects[pi]
    const validImages = project.images
      .filter((img) => img.filename && img.ext && img.deviceType)
      .sort(
        (a, b) =>
          DEVICE_ORDER.indexOf(a.deviceType as DeviceType) -
          DEVICE_ORDER.indexOf(b.deviceType as DeviceType),
      )

    const count = Math.max(validImages.length > 0 ? validImages.length : 1, MIN_DWELL)

    // Build padded image list (cycling valid images, or placeholder if none)
    const padded: ProjectImage[] = []
    for (let i = 0; i < count; i++) {
      if (validImages.length > 0) {
        padded.push(validImages[i % validImages.length])
      } else {
        padded.push({
          id: `${project.id}-placeholder-${i}`,
          description: null,
          filename: null,
          ext: null,
          tags: [],
          deviceType: "desktop",
        })
      }
    }

    // Forward inner: i===0 is pushed first → isFirstInProject lands at start of batch
    for (let i = 0; i < count; i++) {
      const img = padded[i]
      slides.push({
        img,
        project,
        projectIndex: pi,
        isFirstInProject: i === 0,
        deviceType: validImages.length > 0 ? ((img.deviceType as DeviceType) ?? "desktop") : "desktop",
      })
    }
  }

  return slides
}

/** Build per-project overlay items in the same order WebCarousel shows them. */
function buildProjectItems(project: Project): CarouselItem[] {
  const items: CarouselItem[] = project.images
    .filter((img) => img.filename && img.ext && img.deviceType)
    .sort(
      (a, b) =>
        DEVICE_ORDER.indexOf(a.deviceType as DeviceType) -
        DEVICE_ORDER.indexOf(b.deviceType as DeviceType),
    )
    .map((img) => ({ img, type: img.deviceType as DeviceType }))

  // Match WebCarousel: use 3 placeholder items so prev/next is available
  if (items.length === 0) {
    const types: DeviceType[] = ["desktop", "mobile", "tablet"]
    types.forEach((t) => items.push({
      img: { id: `${project.id}-placeholder-${t}`, description: null, filename: null, ext: null, tags: [], deviceType: t },
      type: t,
    }))
  }
  return items
}

interface UnifiedCarouselProps {
  projects: Project[]
  /** 0-based project index to scroll to on initial mount (page refresh on a project route). */
  initialProjectIndex?: number | null
  /** Called once after layout is set up, exposing a scroll-Y lookup for nav. */
  onReady?: (getScrollYForProject: (projectIndex: number) => number) => void
  suppressActiveProjectSyncRef?: MutableRefObject<boolean>
}

export function UnifiedCarousel({ projects, initialProjectIndex, onReady, suppressActiveProjectSyncRef }: UnifiedCarouselProps) {
  const setActiveSectionIndex = useSectionScrollStore((s) => s.setActiveSectionIndex)

  const scrollRangeRef         = useRef<HTMLDivElement>(null)
  const wrapRef                = useRef<HTMLDivElement>(null)
  const swiperRef              = useRef<SwiperClass | null>(null)
  const activeProjectRef       = useRef(-1)
  const onReadyRef             = useRef(onReady)
  useEffect(() => { onReadyRef.current = onReady }, [onReady])
  // Captured once — used for initial scroll restoration without becoming an effect dependency.
  const initialProjectIndexRef = useRef(initialProjectIndex ?? null)

  const [expandedState, setExpandedState] = useState<{ items: CarouselItem[]; initialIdx: number } | null>(null)

  const slides = useMemo(() => buildSlides(projects), [projects])

  // Index of each project's isFirstInProject slide in the flat array
  const projectFirstSlide = useMemo(() => {
    const map: number[] = []
    for (let i = 0; i < slides.length; i++) {
      if (slides[i].isFirstInProject) map[slides[i].projectIndex] = i
    }
    return map
  }, [slides])

  // Static 3D perspective transform — matches the per-project carousel in ProjectSection
  useEffect(() => {
    if (!wrapRef.current) return
    gsap.set(wrapRef.current, {
      y: "12.5%",
      rotateX: 35,
      rotateY: 30,
      rotateZ: -15,
      transformPerspective: 1400,
    })
  }, [])

  // Ticker-driven sticky scroll → Swiper translate.
  //
  // rangeTop is computed once (and on resize) rather than every frame.
  // Calling getBoundingClientRect() 60×/sec forces layout reflow and can
  // produce unstable values during React renders. Caching it eliminates
  // both the reflow cost and the jitter.
  //
  // The LERP is intentionally removed: Lenis already provides eased scroll
  // (duration: 1.2, exponential ease-out). Adding a second lerp created
  // ~0.5s of lag and accumulated error on top of Lenis's own smoothing.
  // With no ScrollTrigger refresh() to defend against, the lerp serves no
  // purpose and is a net negative.
  useEffect(() => {
    const rangeEl = scrollRangeRef.current
    if (!rangeEl || slides.length === 0) return

    const totalScroll = slides.length * SCROLL_PER_SLIDE

    // ── Cache rangeTop ──────────────────────────────────────────────────────
    // Computed once after layout settles, then updated on resize.
    // Never read inside the per-frame tick to avoid forced reflows.
    let rangeTop = 0
    const computeRangeTop = () => {
      rangeTop = rangeEl.getBoundingClientRect().top + window.scrollY
    }
    // Two RAFs: first lets React finish painting; second lets the browser
    // perform one full layout pass with fonts/images resolved.
    let initRafId = requestAnimationFrame(() => {
      initRafId = requestAnimationFrame(computeRangeTop)
    })
    computeRangeTop() // initial estimate — corrected by the RAFs above

    let resizeTimer: ReturnType<typeof setTimeout> | null = null
    const onResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer)
      resizeTimer = setTimeout(computeRangeTop, 100)
    }
    window.addEventListener("resize", onResize)

    let isInsideCarousel = false

    let sectionDebounceTimer: ReturnType<typeof setTimeout> | null = null
    const clearSectionDebounce = () => {
      if (sectionDebounceTimer) {
        clearTimeout(sectionDebounceTimer)
        sectionDebounceTimer = null
      }
    }

    const onTick = () => {
      const raw = (window.scrollY - rangeTop) / totalScroll
      const wasInside = isInsideCarousel
      isInsideCarousel = raw >= 0 && raw <= 1

      if (!isInsideCarousel) {
        if (wasInside) {
          // Exiting — reset active project so next entry re-fires
          activeProjectRef.current = -1
          clearSectionDebounce()
        }
        return
      }

      if (!wasInside) {
        activeProjectRef.current = -1
      }

      const swiper = swiperRef.current
      if (!swiper) return

      const min = swiper.minTranslate()
      const max = swiper.maxTranslate()
      // progress 0 → slide[0] (newest),  progress 1 → slide[N-1] (oldest)
      swiper.setTranslate(min + raw * (max - min))

      // updateActiveIndex is internal but required to recalculate activeIndex after setTranslate
      ;(swiper as unknown as { updateActiveIndex(): void }).updateActiveIndex()
      swiper.updateSlidesClasses?.()

      if (suppressActiveProjectSyncRef?.current) return

      const slide = slides[swiper.activeIndex]
      if (!slide) return
      if (slide.projectIndex !== activeProjectRef.current) {
        activeProjectRef.current = slide.projectIndex
        // Debounce: wait for scroll to settle on a project before committing
        // so fast scrolls through N projects fire only one panel transition.
        // startTransition marks the state update as non-urgent so React won't
        // block the main thread mid-scroll to process it.
        clearSectionDebounce()
        const capturedIdx = slide.projectIndex
        sectionDebounceTimer = setTimeout(() => {
          startTransition(() => setActiveSectionIndex(1 + capturedIdx))
          sectionDebounceTimer = null
        }, 150)
      }
    }
    gsap.ticker.add(onTick)

    // Scroll-Y for a given project: cached rangeTop + progress * totalScroll.
    const getScrollYForProject = (projectIndex: number): number => {
      const swiper = swiperRef.current
      const firstIdx = projectFirstSlide[projectIndex] ?? 0
      const n = slides.length

      if (swiper && swiper.snapGrid?.length > firstIdx) {
        const min = swiper.minTranslate()
        const max = swiper.maxTranslate()
        const targetTranslate = -swiper.snapGrid[firstIdx]
        const progress = (targetTranslate - min) / (max - min)
        return rangeTop + Math.round(Math.max(0, progress) * n * SCROLL_PER_SLIDE)
      }

      // Fallback: linear approximation
      const scrollOffset = n <= 1 ? 0 : Math.round(firstIdx / (n - 1) * n * SCROLL_PER_SLIDE)
      return rangeTop + scrollOffset
    }
    // Restore scroll position when refreshing on a project route.
    // Must happen BEFORE onReady so the parent's getScrollYForProjectRef doesn't
    // race ahead. window.scrollTo is called natively (synchronous) so the position
    // is set immediately and survives StrictMode's destroy+recreate cycle —
    // Lenis defers its scroll to the next RAF tick, which fires AFTER StrictMode
    // cleanup and would land on the destroyed Lenis instance. globalLenis.scrollTo
    // is called in addition to sync Lenis's internal animatedScroll target.
    if (initialProjectIndexRef.current !== null) {
      const idx = initialProjectIndexRef.current
      initialProjectIndexRef.current = null // consume once — prevents double-scroll on StrictMode remount
      const y = getScrollYForProject(idx)
      window.scrollTo(0, y)
      globalLenis?.scrollTo(y, { immediate: true })
    }

    onReadyRef.current?.(getScrollYForProject)

    return () => {
      gsap.ticker.remove(onTick)
      clearSectionDebounce()
      cancelAnimationFrame(initRafId)
      window.removeEventListener("resize", onResize)
      if (resizeTimer) clearTimeout(resizeTimer)
    }
  }, [slides, projectFirstSlide, setActiveSectionIndex, suppressActiveProjectSyncRef])

  const handleSlideClick = (slide: UnifiedSlide) => {
    const items = buildProjectItems(slide.project)
    const initialIdx = items.findIndex((item) => item.img.id === slide.img.id)
    setExpandedState({ items, initialIdx: Math.max(0, initialIdx) })
  }

  if (slides.length === 0) return null

  const totalScroll = slides.length * SCROLL_PER_SLIDE

  return (
    <>
      <style>{`
        [data-unified-carousel] .swiper-slide {
          opacity: 0.3;
          transition: opacity 0.3s ease;
        }
        [data-unified-carousel] .swiper-slide-active {
          opacity: 1;
        }
        [data-unified-carousel] .slide-feature {
          opacity: 0.5;
          transition: opacity 0.3s ease;
        }
        [data-unified-carousel] .swiper-slide-active .slide-feature {
          opacity: 1;
        }
      `}</style>

      {/* Outer div provides the scroll distance — sticky child stays in viewport */}
      <div
        ref={scrollRangeRef}
        data-carousel-scroll-range
        style={{ height: `calc(${totalScroll}px + 100vh)` }}
      >
        <div
          data-unified-carousel
          style={{ position: "sticky", top: 0, height: "100vh" }}
        >
          {/* Vertically centers the carousel within the sticky viewport */}
          <div style={{ height: "100vh", display: "flex", alignItems: "center" }}>
            {/* GSAP applies 3D perspective transform here; width: 100% needed in flex context */}
            <div ref={wrapRef} style={{ width: "100%" }}>
              <Swiper
                slidesPerView="auto"
                spaceBetween={24}
                centeredSlides={true}
                allowTouchMove={false}
                // observer:false — prevents Swiper's MutationObserver from firing
                // on updateSlidesClasses() DOM mutations. Without this, every class
                // change triggers observerUpdate → onResize → slideTo(activeIndex,0),
                // which overwrites our scroll-driven translate for one frame (visible jump).
                observer={false}
                // speed:0 — ensures any remaining internal slideTo (e.g. from window
                // resize) uses 0ms duration, never fighting our translate with CSS animation.
                speed={0}
                style={{ overflow: "visible", height: CAROUSEL_H + FEATURE_TEXT_H }}
                onSwiper={(s) => { swiperRef.current = s }}
              >
                {slides.map((slide, idx) => (
                  <SwiperSlide
                    key={`${slide.project.id}-${slide.img.id}-${idx}`}
                    style={{ width: SLIDE_WIDTHS[slide.deviceType] }}
                  >
                    <div
                      style={{ height: CAROUSEL_H, transform: "perspective(1200px)", cursor: "pointer" }}
                      onClick={() => handleSlideClick(slide)}
                    >
                      <DeviceFrame
                        type={slide.deviceType}
                        images={[slide.img]}
                        activeIdx={0}
                        className="h-full w-full"
                      />
                    </div>
                    {/* Feature label — placeholder until real feature data is available */}
                    {slide.img.description && (
                      <div
                        className="slide-feature mt-4 select-none"
                        style={{ paddingLeft: FRAME_PADDING + 2 }}
                      >
                        <p className="mt-1 text-xs leading-relaxed text-foreground/35">
                          {slide.img.description}
                        </p>
                      </div>
                    )}
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      </div>

      {expandedState && createPortal(
        <ExpandedOverlay
          items={expandedState.items}
          initialIdx={expandedState.initialIdx}
          onClose={() => setExpandedState(null)}
        />,
        document.body,
      )}
    </>
  )
}
